/**
 * Charm Builder — State
 * Single global state object. All UI derives from CharmBuilder.state.
 * Persists to localStorage key 'charm-builder-v1' on every mutation.
 */

window.CharmBuilder = (() => {
  const STORAGE_KEY = 'charm-builder-v1';

  const DEFAULT_STATE = {
    step: 1,
    base: null,
    charms: [],
    activeMetal: null,
    activeBaseTab: 0,
    activeCharmTab: 0,
  };

  let state = { ...DEFAULT_STATE };
  let cachedDiscountTiers = null;

  // --------------------------------------------------------------------------
  // Internal helpers
  // --------------------------------------------------------------------------

  function _dispatch() {
    document.dispatchEvent(new CustomEvent('charm-builder:updated', { detail: { state } }));
  }

  function _isValidState(s) {
    return (
      s &&
      typeof s === 'object' &&
      [1, 2, 3].includes(s.step) &&
      Array.isArray(s.charms)
    );
  }

  // --------------------------------------------------------------------------
  // Public API
  // --------------------------------------------------------------------------

  function setState(patch) {
    state = { ...state, ...patch };
    persist();
    _dispatch();
  }

  function addCharm(charm) {
    const instanceId = `charm-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    state = { ...state, charms: [...state.charms, { ...charm, instanceId, x: 0, y: 0 }] };
    persist();
    _dispatch();
  }

  function removeCharm(instanceId) {
    state = { ...state, charms: state.charms.filter((c) => c.instanceId !== instanceId) };
    persist();
    _dispatch();
  }

  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {}
  }

  function restore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!_isValidState(parsed)) return false;
      state = parsed;
      return true;
    } catch (_) {
      return false;
    }
  }

  function reset() {
    state = { ...DEFAULT_STATE };
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
    _dispatch();
  }

  /**
   * Tiers come from whichever builder_step metaobject has a non-blank
   * discount_tiers list (see sections/charm-builder.liquid / the
   * discount_step lookup there). Empty array when no step configures any —
   * the discount bar is then hidden and this always returns 0.
   */
  function getDiscountTiers() {
    if (cachedDiscountTiers) return cachedDiscountTiers;
    const steps = (window.__BUILDER_DATA__ && window.__BUILDER_DATA__.steps) || [];
    const found = steps.find(s => Array.isArray(s.discountTiers) && s.discountTiers.length > 0);
    cachedDiscountTiers = found ? found.discountTiers : [];
    return cachedDiscountTiers;
  }

  /** Highest tier percent whose threshold the current charm count meets. */
  function getDiscountPercent() {
    const count = state.charms.reduce((sum, c) => sum + (c.quantity || 1), 0);
    return getDiscountTiers().reduce((pct, tier) => {
      return count >= tier.threshold && tier.percent > pct ? tier.percent : pct;
    }, 0);
  }

  /**
   * Restore link for a build — the inverse of the ?cb_base= / ?cb_charms=
   * handling in charm-builder.js. One cb_charms entry per canvas unit, as
   * variantId:x:y:rotation, with x/y rounded to whole percent exactly like
   * the _position_x/_position_y cart properties in charm-builder-cart.js.
   * Returns a root-relative path + query, or null when no base is selected.
   * Used by awe-charm-builder-klaviyo.js for the abandonment email link.
   */
  function toRestoreUrl(s = state) {
    if (!s || !s.base || !s.base.variantId) return null;
    const params = new URLSearchParams();
    params.set('cb_base', String(s.base.variantId));
    const charms = [];
    (s.charms || []).forEach((c) => {
      const entry = [
        c.variantId,
        Math.round(c.x || 0),
        Math.round(c.y || 0),
        ((Math.round(c.rotation || 0) % 360) + 360) % 360,
      ].join(':');
      for (let i = 0; i < Math.max(1, c.quantity || 1); i++) charms.push(entry);
    });
    if (charms.length) params.set('cb_charms', charms.join(','));
    const path = window.location.pathname;
    // URLSearchParams encodes ':' and ',' — decode them back so the link stays readable.
    return `${path}?${params.toString().replace(/%3A/gi, ':').replace(/%2C/gi, ',')}`;
  }

  /**
   * Total tier savings in cents for the current set. The tier is picked by
   * charm count only (getDiscountPercent), but the discount function applies
   * that percent to every _charmbuilderitem line — base included. Rounded
   * per line to match how Shopify allocates a percentage discount per line.
   */
  function getDiscountCents() {
    const pct = getDiscountPercent();
    if (!pct) return 0;
    const lineDiscount = cents => Math.round((cents * pct) / 100);
    const baseCents = state.base ? lineDiscount(state.base.price || 0) : 0;
    return state.charms.reduce((sum, c) => sum + lineDiscount((c.price || 0) * (c.quantity || 1)), baseCents);
  }

  return { get state() { return state; }, setState, addCharm, removeCharm, persist, restore, reset, getDiscountPercent, getDiscountTiers, getDiscountCents, toRestoreUrl };
})();
